import { ArrowUpIcon } from '@heroicons/react/20/solid';
import { Alert, CircularProgress, LinearProgress } from '@mui/joy';

import { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebounce } from 'use-debounce';

import { KeyboardShortcut } from '@/Pages/KeyboardShortcutsSidePane';
import { useFeedbackSourceUser } from '@/hooks/useFeedbackSourceUser';
import { useCreateFeedback, useFeedbacksInfinite } from '@/hooks/useSwr';
import useToastOn429Error from '@/hooks/useToastOn429Error';
import { FeedbackSchema } from '@/types/schema';
import { apiFeedbackPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';
import { localTime } from '@/utils/utc-time';

import { Textarea } from '../../design-system/components/Textarea/Textarea';
import TrashIcon from '../../icons/DetailedTrashIcon.svg?react';
import { ProfileCircle } from '../ProfileCircle';
import { Skeleton } from '../Skeleton';

const PAGE_SIZE = 10;

function RunNote({
  note,
  onNoteDeleted,
}: {
  note: FeedbackSchema;
  onNoteDeleted: () => void;
}) {
  const { trigger, isMutating } = useCreateFeedback();

  const momentPosted = localTime(note.created_at);

  const { isActiveUser, feedbackUser, isLoading } = useFeedbackSourceUser(
    note.feedback_source.user_id
  );
  const username =
    feedbackUser?.full_name ?? note.feedback_source.user_name ?? 'User';

  if (isLoading) {
    return (
      <div
        className="flex flex-1 flex-col items-stretch gap-3 py-3"
        data-testid="run-note"
      >
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col items-stretch gap-3 py-3"
      data-testid="run-note"
    >
      <div className="flex flex-row items-center justify-start gap-1.5">
        <div className="flex-0">
          <ProfileCircle active={isActiveUser} name={username} />
        </div>
        <div className="text-xs font-semibold leading-none text-primary">
          {username}
        </div>
        <div className="grow" />
        <div className="text-xs font-normal leading-none text-tertiary">
          {momentPosted.fromNow()}
        </div>
      </div>
      <div className="flex flex-1 flex-col items-stretch gap-3">
        <div className="overflow-wrap whitespace-pre-line break-words text-xs font-normal leading-none text-primary">
          {note.comment}
        </div>
        <div className="flex flex-row items-baseline justify-start gap-1.5">
          <div className="grow" />
          {!isActiveUser ? null : isMutating ? (
            <CircularProgress
              thickness={2}
              sx={{
                '--CircularProgress-size': '16px',
              }}
            />
          ) : (
            <TrashIcon
              className="stroke-secondary h-4 cursor-pointer"
              onClick={() => {
                trigger(
                  {
                    method: 'DELETE',
                    url: `${apiFeedbackPath}/${note.id}`,
                  },
                  {
                    onSuccess: () => {
                      onNoteDeleted();
                    },
                  }
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function NoteTextBox({
  runId,
  onNoteAdded,
}: {
  runId: string;
  onNoteAdded: () => void;
}) {
  const [newNote, setNewNote] = useState('');

  const newNoteRef = useRef<HTMLTextAreaElement>(null);

  const { trigger, isMutating, error } = useCreateFeedback();
  const { handle429Error, is429Error } = useToastOn429Error();

  const [isMutatingDebounced] = useDebounce(isMutating, 1000, {
    leading: true,
  });

  const focusOnElement = (event: KeyboardEvent) => {
    event.preventDefault();
    if (document.activeElement !== newNoteRef.current) {
      newNoteRef.current?.focus();
    }
  };

  useHotkeys('n', (event) => focusOnElement(event));

  const onSubmit = async () => {
    if (isMutating || !newNote) return;
    try {
      await trigger(
        {
          json: {
            run_id: runId,
            key: 'note',
            comment: newNote,
            feedback_source: { type: 'app' },
            feedback_config: { type: 'freeform' },
          },
        },
        {
          onSuccess: () => {
            onNoteAdded();
          },
        }
      );
      setNewNote('');
    } catch (e) {
      handle429Error(e as Error, 'feedback');
    }
  };
  const errorMessage = error?.message;

  return (
    <div className="flex flex-col rounded-lg bg-secondary px-4 py-2.5">
      <div className="flex flex-row items-center gap-2">
        <Textarea
          ref={newNoteRef}
          value={newNote}
          onChange={setNewNote}
          placeholder="Add your note..."
          disabled={isMutating}
          debounceMs={0}
          rows={2}
          resize="vertical"
          onKeyDown={(event) => {
            if (
              event.key === 'NumpadEnter' &&
              !event.shiftKey &&
              !isMutatingDebounced
            ) {
              event.preventDefault();
              onSubmit();
            }
            if (event.key === 'Escape') {
              event.stopPropagation();
              newNoteRef.current?.blur();
            }
          }}
        />
        {!newNote && <KeyboardShortcut keys={['N']} className="w-[26px]" />}
        {newNote && (
          <button
            type="button"
            onClick={onSubmit}
            disabled={isMutatingDebounced}
            className={cn(
              'rounded-full bg-brand-primary p-1',
              isMutatingDebounced ? 'animate-pulse opacity-75' : 'hover:ring'
            )}
            data-testid="add-note-button"
          >
            <ArrowUpIcon
              className={cn('h-4 w-4 rounded-full text-button-primary')}
            />
          </button>
        )}
      </div>
      {errorMessage && !is429Error(error) && (
        <div className="pb-1 pt-2 text-xs text-error">
          Error: {errorMessage}
        </div>
      )}
    </div>
  );
}
function InnerRunNotes({
  runId,
  sesssionId,
  title = 'Notes',
}: {
  runId: string;
  sesssionId: string;
  title?: string;
}) {
  const {
    data: notePages,
    isLoading,
    error,
    size,
    setSize,
    mutate,
  } = useFeedbacksInfinite(PAGE_SIZE, {
    key: 'note',
    run: runId,
    session: sesssionId,
    source: 'app',
    has_comment: true,
    has_score: false,
  });

  if (error)
    return (
      <Alert variant="outlined" color="danger">
        {error.message}
      </Alert>
    );
  if (!notePages && isLoading) return <LinearProgress />;
  if (!notePages) {
    return null;
  }

  const visibleNotes = notePages.flat().reverse();

  const totalNotes = Number(notePages[0].headers?.['x-pagination-total'] ?? 0);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-9">
      <div className="flex w-full flex-col items-stretch justify-start gap-3 self-stretch">
        <div className="flex flex-col items-start justify-start">
          <div className="flex flex-row text-base font-semibold">
            <span className="my-auto">{title}</span>
          </div>
        </div>
        {totalNotes > visibleNotes.length && (
          <div
            onClick={() => setSize(size + 1)}
            className="my-2 cursor-pointer text-xs font-semibold leading-none text-brand-green-500"
          >
            See more notes
          </div>
        )}
        <div
          className={cn(
            'flex w-full flex-col items-stretch justify-start divide-y divide-secondary'
          )}
        >
          {visibleNotes.map((c, i) => {
            return <RunNote key={i} note={c} onNoteDeleted={mutate} />;
          })}
          <div className={cn(totalNotes > 0 ? 'pt-5' : 'pt-0')}>
            <NoteTextBox runId={runId} onNoteAdded={mutate} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function RunNotesCrudPanel(props: {
  runId: string;
  sessionId: string;
  title?: string;
}) {
  return (
    <div className="flex max-w-full grow flex-col self-stretch overflow-hidden bg-inherit">
      <InnerRunNotes
        runId={props.runId}
        sesssionId={props.sessionId}
        title={props.title}
      />
    </div>
  );
}
